import { useCallback, useEffect, useId, useRef, useState, type FocusEvent } from 'react';

interface CalendarPopoverOptions {
  /** Hears every open and close, after the render that made it. */
  onOpenChange?: (open: boolean) => void;
  /**
   * Focus has left BOTH the field and the calendar card (Tab past the last input, a click
   * elsewhere) — the end of an edit. Called before the calendar closes.
   */
  onLeave?: () => void;
}

/**
 * The open/close and focus bookkeeping every field in the family shares.
 *
 * Focus decides when the calendar goes away: it closes once focus has left BOTH the field
 * and the calendar card. Moving between the field's inputs, the calendar toggle and the
 * grid keeps it open.
 *
 * `onOpenChange` hears every open and close, after the render that made it — so a host
 * reading the field's value on close sees the pick that closed it.
 */
export function useCalendarPopover({ onOpenChange, onLeave }: CalendarPopoverOptions = {}) {
  const [open, setOpen] = useState(false);
  const onOpenChangeRef = useRef(onOpenChange);
  const onLeaveRef = useRef(onLeave);
  const reportedOpen = useRef(open);
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
    onLeaveRef.current = onLeave;
  });
  useEffect(() => {
    if (reportedOpen.current === open) return;
    reportedOpen.current = open;
    onOpenChangeRef.current?.(open);
  }, [open]);
  /** The panel whose day takes focus when the calendar opens — the keyboard route in. */
  const [focusPanelOnOpen, setFocusPanelOnOpen] = useState<number | null>(null);
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const paperRef = useRef<HTMLDivElement | null>(null);
  const popperId = `calendar-${useId().replace(/:/g, '')}`;

  const contains = useCallback(
    (node: unknown) =>
      node instanceof Node &&
      Boolean(fieldRef.current?.contains(node) || paperRef.current?.contains(node)),
    [],
  );

  /** Opens; `focusPanel` moves focus straight into that panel's grid. */
  const openCalendar = useCallback((focusPanel: number | null = null) => {
    setFocusPanelOnOpen(focusPanel);
    setOpen(true);
  }, []);

  /** Closes, and puts focus back on `returnTo` if it was inside the calendar (else it would drop to <body>). */
  const closeCalendar = useCallback((returnTo?: HTMLElement | null) => {
    if (returnTo && paperRef.current?.contains(document.activeElement)) returnTo.focus();
    setOpen(false);
    setFocusPanelOnOpen(null);
  }, []);

  /** Into a panel's grid while the calendar is already open (ArrowDown from an input). */
  const focusGrid = useCallback((panel = 0) => {
    const paper = paperRef.current;
    const target =
      paper?.querySelector<HTMLElement>(`[data-panel="${panel}"] .rdp-day_button[tabindex="0"]`) ??
      paper?.querySelector<HTMLElement>('.rdp-day_button[tabindex="0"]');
    target?.focus();
  }, []);

  /** Whether focus is in the calendar card — a pick made from the keyboard. */
  const focusInCalendar = useCallback(
    () => Boolean(paperRef.current?.contains(document.activeElement)),
    [],
  );

  const onBlurWithin = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (contains(event.relatedTarget)) return;
      onLeaveRef.current?.();
      setOpen(false);
      setFocusPanelOnOpen(null);
    },
    [contains],
  );

  return {
    open,
    focusPanelOnOpen,
    fieldRef,
    paperRef,
    popperId,
    openCalendar,
    closeCalendar,
    focusGrid,
    focusInCalendar,
    onBlurWithin,
  };
}
